import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Calendar, MapPin, Plus, Video } from "lucide-react";

export default function EventsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [category, setCategory] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name:"",description:"",starts_at:"",ends_at:"",location:"",is_online:false,url:"",category:"Community",privacy:"public" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/events`, { params: { filter, category } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, category]);

  const create = async () => {
    setErr("");
    try { const { data } = await api.post(`/events`, form); setCreating(false); toast.success("Event created"); await load(); if (data?.id) window.location.assign(`/events/${data.id}`); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const rsvp = async (e, status) => {
    try { await api.post(`/events/${e.id}/rsvp?status=${status}`); toast.success(`RSVP: ${status.replace("_"," ")}`); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="events-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Events</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Events</h1>
          </div>
          <button onClick={() => setCreating(true)} className="csc-btn-primary" data-testid="create-event-btn"><Plus size={13} /> New Event</button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {["upcoming","this_week","all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`csc-pill ${filter===f?"csc-pill-active":""}`} data-testid={`filter-${f}`}>{f.replace("_"," ")}</button>
          ))}
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="csc-input !py-2 max-w-[180px]" data-testid="event-category-filter">
            <option value="">All categories</option>
            {["Cybersecurity","Nonprofit","Education","Community","Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>

        {creating && (
          <div className="csc-card p-5 space-y-3" data-testid="create-event-form">
            <input value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Event name" className="csc-input" data-testid="event-name-input" />
            <textarea value={form.description} onChange={(e) => setForm({...form, description:e.target.value})} placeholder="Description" rows={3} className="csc-input resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="csc-label">Starts</label><input type="datetime-local" value={form.starts_at} onChange={(e)=>setForm({...form, starts_at:e.target.value})} className="csc-input" data-testid="event-start-input" /></div>
              <div><label className="csc-label">Ends</label><input type="datetime-local" value={form.ends_at} onChange={(e)=>setForm({...form, ends_at:e.target.value})} className="csc-input" data-testid="event-end-input" /></div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#94a3b8]">
              <input type="checkbox" checked={form.is_online} onChange={(e)=>setForm({...form, is_online:e.target.checked})} className="accent-[#00aaff]" /> Online event
            </label>
            <input value={form.is_online ? form.url : form.location} onChange={(e) => setForm({...form, [form.is_online?"url":"location"]:e.target.value})} placeholder={form.is_online?"Meeting URL":"Address"} className="csc-input" data-testid="event-location-input" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className="csc-input">
                {["Cybersecurity","Nonprofit","Education","Community","Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <select value={form.privacy} onChange={(e)=>setForm({...form,privacy:e.target.value})} className="csc-input">
                <option value="public">Public</option><option value="invite">Invite only</option>
              </select>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            <div className="flex justify-end gap-2"><button onClick={()=>setCreating(false)} className="csc-btn-ghost">Cancel</button><button onClick={create} className="csc-btn-primary" data-testid="event-create-submit">Create</button></div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="events-empty">No events match these filters.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((e) => (
              <div key={e.id} className="csc-card p-5 hover:border-[#39ff14]/50 transition-colors" data-testid={`event-${e.id}`}>
                <Link to={`/events/${e.id}`} className="block" data-testid={`event-link-${e.id}`}>
                  <div className="flex items-center gap-2"><Calendar size={14} className="text-[#39ff14]" /><span className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">{e.category}</span></div>
                  <h3 className="text-base font-semibold mt-2">{e.name}</h3>
                  <p className="text-xs text-[#94a3b8] mt-1 line-clamp-2">{e.description}</p>
                  <div className="font-mono-tech text-[11px] text-[#94a3b8] mt-2 flex items-center gap-2">
                    {e.is_online ? <Video size={11} className="text-[#00aaff]" /> : <MapPin size={11} />}
                    {e.is_online ? "Online" : e.location} · {new Date(e.starts_at).toLocaleString()}
                  </div>
                </Link>
                <div className="mt-3 flex items-center justify-between text-[11px] font-mono-tech text-[#94a3b8]">
                  <span>{e.going} going · {e.interested} interested</span>
                </div>
                <div className="mt-2 flex gap-1">
                  {["going","interested","not_going"].map((s) => (
                    <button key={s} onClick={() => rsvp(e, s)}
                      className={`csc-pill ${e.rsvp === s ? "csc-pill-active" : ""}`}
                      data-testid={`rsvp-${e.id}-${s}`}>{s.replace("_"," ")}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
